import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
} from '@mui/material';
import { Visibility, Add } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { projectListPrimaryLabel } from '../../utils/projectIdentityFields';
import { useProject } from '../../context/ProjectContext';
import { projectService } from '../../services/projectService';
import amendmentVersionService from '../../services/amendmentVersion.service';
import clinicalIntakeService from '../../services/clinicalIntake.service';
import documentEditorService from '../../services/documentEditor.service';
import sapIntakeService from '../../sap/services/sapIntake.service';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { IND_ENABLED } from '../../config/env';

const StudyDashboard = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const [docStatuses, setDocStatuses] = useState({
    protocol: 'not_initiated',
    icf: 'not_initiated',
    sap: 'not_initiated',
    ind: 'not_initiated',
  });
  const { currentProject, loadProject } = useProject();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const projectIdFromUrl = searchParams.get('project');

  useEffect(() => {
    fetchStudies();
  }, [currentProject, projectIdFromUrl]);

  useEffect(() => {
    if (studies.length > 0) {
      loadVersionsAndStatuses(studies[0]._id);
    }
  }, [studies]);

  const normalizeStatus = (status, fallback = 'not_initiated') => {
    const value = String(status || '').trim().toLowerCase();
    if (!value) return fallback;
    return value;
  };

  const fetchStudies = async () => {
    try {
      setLoading(true);
      setError(null);

      const projectId = projectIdFromUrl;

      if (projectId) {
        const response = await projectService.getProject(projectId);
        const projectData = response.data;
        setStudies([projectData]);

        if (!currentProject || currentProject._id !== projectId) {
          await loadProject(projectId);
        }
      } else if (currentProject) {
        setStudies([currentProject]);
      } else {
        const currentProjectId = localStorage.getItem('currentProjectId');
        if (currentProjectId) {
          const response = await projectService.getProject(currentProjectId);
          setStudies([response.data]);
        } else {
          setStudies([]);
          setError('No study selected. Please select a study first.');
        }
      }
    } catch (err) {
      setError('Failed to fetch study');
      setStudies([]);
    } finally {
      setLoading(false);
    }
  };

  const hasStartedSections = (sections) => {
    if (!Array.isArray(sections) || sections.length === 0) return false;
    return sections.some((s) => {
      const output = String(s?.output || s?.content || '').trim();
      const versions = Array.isArray(s?.versions) ? s.versions : [];
      const hasVersionContent = versions.some((v) => String(v?.output || '').trim());
      // Any saved section (or content) means the document was initiated
      return Boolean(s?._id || s?.title || output || hasVersionContent);
    });
  };

  const promoteIfNotInitiated = (current, shouldPromote) => {
    if (!shouldPromote) return current;
    return normalizeStatus(current) === 'not_initiated' ? 'draft' : current;
  };

  /**
   * Load amendment versions + actual Protocol/SAP/ICF status.
   * Prefer latest amendment version status; fall back to clinical intake fields;
   * if ICF/SAP sections (or SAP intake) exist, treat not_initiated as draft.
   */
  const loadVersionsAndStatuses = async (projectId) => {
    if (!projectId) return;

    let nextStatuses = {
      protocol: 'not_initiated',
      icf: 'not_initiated',
      sap: 'not_initiated',
      ind: 'not_initiated',
    };

    try {
      const intake = await clinicalIntakeService.getLatestClinicalIntake(projectId);
      const data = intake?.data || intake || {};
      nextStatuses = {
        protocol: normalizeStatus(data.protocolStatus, 'not_initiated'),
        icf: normalizeStatus(data.icfStatus, 'not_initiated'),
        sap: normalizeStatus(data.sapStatus, 'not_initiated'),
        // IND has no amendment-version flow; its status comes only from
        // markDocumentInitiated(projectId, 'IND') on the first section initialize.
        ind: normalizeStatus(data.indStatus, 'not_initiated'),
      };
    } catch (err) {
      console.warn('StudyDashboard: could not load clinical intake statuses', err?.message || err);
    }

    const documentTypes = ['PROTOCOL', 'SAP', 'ICF'];
    const versions = {};

    for (const docType of documentTypes) {
      try {
        const response = await amendmentVersionService.getAmendmentVersions(projectId, docType);
        versions[docType] = response.success && response.data ? response.data : [];
      } catch (err) {
        console.error(`Failed to fetch ${docType} versions:`, err);
        versions[docType] = [];
      }
    }

    const pickLatest = (docType, key) => {
      const list = versions[docType] || [];
      if (!list.length) return;
      const latest = [...list].sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))[0];
      if (latest?.status) nextStatuses[key] = normalizeStatus(latest.status, nextStatuses[key]);
    };
    pickLatest('PROTOCOL', 'protocol');
    pickLatest('SAP', 'sap');
    pickLatest('ICF', 'icf');

    // Infer draft when work has started but clinical intake status was never updated
    let protocolStarted = false;
    let icfStarted = false;
    let sapStarted = false;
    try {
      const protocolSections = await documentEditorService.getAllSections(projectId);
      protocolStarted = hasStartedSections(protocolSections);
    } catch (err) {
      console.warn('StudyDashboard: could not load Protocol sections', err?.message || err);
    }
    try {
      const icfSections = await documentEditorService.getAllICFSections(projectId, { includeAll: true });
      icfStarted = hasStartedSections(icfSections);
    } catch (err) {
      console.warn('StudyDashboard: could not load ICF sections', err?.message || err);
    }
    try {
      const sapSections = await documentEditorService.getAllSAPSections(projectId);
      sapStarted = hasStartedSections(sapSections);
    } catch (err) {
      console.warn('StudyDashboard: could not load SAP sections', err?.message || err);
    }
    if (!sapStarted) {
      try {
        const sapIntake = await sapIntakeService.fetchLatestSAPIntakeData(projectId);
        sapStarted = Boolean(sapIntake && (sapIntake._id || sapIntake.id || Object.keys(sapIntake).length > 0));
      } catch (err) {
        // No SAP intake yet is fine
      }
    }

    nextStatuses.protocol = promoteIfNotInitiated(nextStatuses.protocol, protocolStarted);
    nextStatuses.icf = promoteIfNotInitiated(nextStatuses.icf, icfStarted);
    nextStatuses.sap = promoteIfNotInitiated(nextStatuses.sap, sapStarted);

    // Persist healed statuses so other views stay in sync
    if (protocolStarted && normalizeStatus(nextStatuses.protocol) === 'draft') {
      clinicalIntakeService.markDocumentInitiated(projectId, 'PROTOCOL').catch(() => {});
    }
    if (icfStarted && normalizeStatus(nextStatuses.icf) === 'draft') {
      clinicalIntakeService.markDocumentInitiated(projectId, 'ICF').catch(() => {});
    }
    if (sapStarted && normalizeStatus(nextStatuses.sap) === 'draft') {
      clinicalIntakeService.markDocumentInitiated(projectId, 'SAP').catch(() => {});
    }

    setDocStatuses(nextStatuses);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewProtocol = (study) => {
    navigate(`/document-editor/protocol/${study._id}?projectId=${study._id}`);
  };

  const handleViewICF = (study) => {
    navigate(`/document-editor/icf/${study._id}?projectId=${study._id}`);
  };

  const handleViewSAP = (study) => {
    navigate(`/document-editor/sap/${study._id}?projectId=${study._id}`);
  };

  const handleViewIND = (study) => {
    navigate(`/clinical-intake?project=${study._id}&tab=ind`);
  };

  const renderDocActions = (study, statusKey, onView) => {
    const status = normalizeStatus(docStatuses[statusKey]);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StatusBadge status={status} />
        <IconButton onClick={() => onView(study)} size="small" aria-label={`view ${statusKey}`}>
          <Visibility fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  if (loading) {
    return <LoadingState label="Loading studies…" />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={fetchStudies} />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {studies.length > 0 ? projectListPrimaryLabel(studies[0]) : 'No Study Selected'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {IND_ENABLED ? 'Protocol, SAP, ICF, and IND status for this study.' : 'Protocol, SAP, and ICF status for this study.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/amendment')}
        >
          Amendment
        </Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Protocol</TableCell>
              <TableCell>SAP</TableCell>
              <TableCell>ICF</TableCell>
              {IND_ENABLED && <TableCell>IND</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {studies.length > 0 ? (
              studies
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((study) => (
                  <TableRow key={study._id}>
                    <TableCell>
                      {renderDocActions(study, 'protocol', handleViewProtocol)}
                    </TableCell>
                    <TableCell>
                      {renderDocActions(study, 'sap', handleViewSAP)}
                    </TableCell>
                    <TableCell>
                      {renderDocActions(study, 'icf', handleViewICF)}
                    </TableCell>
                    {IND_ENABLED && (
                      <TableCell>
                        {renderDocActions(study, 'ind', handleViewIND)}
                      </TableCell>
                    )}
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={IND_ENABLED ? 4 : 3} sx={{ border: 'none' }}>
                  <EmptyState compact title="No studies found" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={studies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default StudyDashboard;
